import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Route, Loader2, Play, Flag, ClipboardCheck, Check } from 'lucide-react';
import { portalService, ViagemDoDia } from '../../services/portal';
import { KmInput } from '../../components/MaskedInputs';
import DicaUltimoKm from '../../components/DicaUltimoKm';
import { erroDeKmMenor, useUltimoKm } from '../../hooks/useUltimoKm';
import { formatKilometers } from '../../utils/formatters';

/**
 * A viagem de hoje, e o que o motorista faz com ela.
 *
 * Só o dia de hoje, e uma viagem de cada vez. Com a viagem fixa, um motorista
 * passa a ter uma viagem cadastrada para cada dia do mês: mostrar todas
 * transforma a tela num calendário onde ele precisa procurar a de hoje — e,
 * pior, permite iniciar por engano a de quinta-feira. Quem decide o que aparece
 * é o servidor; a tela não teria como sem baixar o mês inteiro.
 *
 * A ordem dos botões é a ordem do dia: conferir o veículo, sair, voltar. Cada
 * viagem mostra um só, o do passo em que ela está.
 */

const paraNumero = (texto: string): number => {
  const n = parseFloat(String(texto ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/** "Origem → Parada → Destino", com as paradas na ordem da viagem. */
const trajeto = (viagem: ViagemDoDia): string =>
  [viagem.origem, ...(viagem.paradas ?? []).map((p) => p.endereco), viagem.destino].join(' → ');

const ViagensDoMotorista: React.FC = () => {
  const navigate = useNavigate();
  const [aberta, setAberta] = useState<{ viagem: ViagemDoDia; momento: 'iniciar' | 'finalizar' } | null>(
    null,
  );
  const [kmSaida, setKmSaida] = useState('');
  const [kmChegada, setKmChegada] = useState('');
  const [salvando, setSalvando] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['portal', 'viagens', 'hoje'],
    queryFn: portalService.getViagensDeHoje,
  });

  const viagens: ViagemDoDia[] = data?.data ?? [];

  const abrir = (viagem: ViagemDoDia, momento: 'iniciar' | 'finalizar') => {
    setKmSaida('');
    setKmChegada('');
    setAberta({ viagem, momento });
  };

  // O ultimo km do painel vem preenchido: o motorista confere com o painel e
  // corrige para cima se rodou desde o ultimo registro. Digitar do zero era o
  // que produzia 200.000 num veiculo em 103.432.
  const { ultimo, carregando: buscandoKm } = useUltimoKm(aberta?.viagem.veiculo_id, 'portal');

  useEffect(() => {
    if (!aberta || !ultimo) return;
    const sugerido = String(Math.round(ultimo.km));
    if (aberta.momento === 'iniciar' && kmSaida === '') {
      setKmSaida(sugerido);
    }
    if (aberta.momento === 'finalizar' && kmChegada === '') {
      // Na chegada o piso e o maior entre o ultimo registro e a propria saida.
      const saida = aberta.viagem.km_saida != null ? Number(aberta.viagem.km_saida) : 0;
      setKmChegada(String(Math.round(Math.max(ultimo.km, saida))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberta?.viagem.id, aberta?.momento, ultimo?.km]);

  const erroKmSaida = erroDeKmMenor(kmSaida, ultimo);
  const erroKmChegada = erroDeKmMenor(kmChegada, ultimo);

  // A viagem que a gestão iniciou pela tela dela não passou pelo portal e pode
  // não ter km de saída. Sem ele não há percorrido, então o campo aparece aqui —
  // e só aqui, para não estorvar quem já informou na partida.
  const faltaKmDeSaida = aberta?.momento === 'finalizar' && aberta.viagem.km_saida == null;

  const enviar = async () => {
    if (!aberta) return;

    // O servidor recusa igual; barrar aqui poupa a ida e mostra o motivo no campo.
    if ((aberta.momento === 'iniciar' || faltaKmDeSaida) && erroKmSaida) {
      toast.error(erroKmSaida);
      return;
    }
    if (aberta.momento === 'finalizar' && erroKmChegada) {
      toast.error(erroKmChegada);
      return;
    }

    setSalvando(true);

    try {
      if (aberta.momento === 'iniciar') {
        await portalService.iniciarViagem(aberta.viagem.id, { km_saida: paraNumero(kmSaida) });
        toast.success('Viagem iniciada. Boa viagem!');
      } else {
        await portalService.finalizarViagem(aberta.viagem.id, {
          km_chegada: paraNumero(kmChegada),
          ...(faltaKmDeSaida ? { km_saida: paraNumero(kmSaida) } : {}),
        });
        toast.success('Viagem finalizada.');
      }

      setAberta(null);
      refetch();
    } catch (erro: any) {
      const erros = erro?.response?.data?.errors;
      const primeiro = erros ? (Object.values(erros)[0] as string[])?.[0] : null;
      toast.error(primeiro ?? erro?.response?.data?.message ?? 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <section className="bg-white rounded-xl shadow-sm border p-5">
      <h2 className="text-base font-semibold text-secondary-900 mb-3 flex items-center gap-2">
        <Route className="w-5 h-5 text-secondary-400" /> Minha viagem de hoje
      </h2>

      {isLoading && (
        <div className="flex justify-center py-6">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      )}

      {!isLoading && viagens.length === 0 && (
        <p className="text-sm text-secondary-400">Você não tem viagem programada para hoje.</p>
      )}

      <div className="divide-y divide-secondary-100">
        {viagens.map((v) => (
          <div key={v.id} className="py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              {/* Sem corte: com paradas, o motorista precisa ler o caminho
                  inteiro para saber aonde ir, e no celular nao ha hover. */}
              <p className="text-sm font-medium text-secondary-900 break-words">
                {trajeto(v)}
              </p>
              <p className="text-xs text-secondary-500">
                {v.data_saida ? new Date(v.data_saida).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                {' · '}
                {String(v.status).replace('_', ' ')}
                {v.veiculo?.placa && (
                  <>
                    {' · '}
                    {v.veiculo.placa}
                  </>
                )}
              </p>
              {/* Trocar o botão sozinho não diz o que aconteceu: o motorista
                  acabou de responder 25 itens e precisa ver que chegaram. */}
              {v.status === 'planejada' && v.checklist_feito && (
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-0.5">
                  <Check className="w-3.5 h-3.5" /> Checklist enviado
                </p>
              )}
            </div>

            {/* Um botão só por viagem, o do passo em que ela está: conferir o
                veículo, sair, voltar. */}
            {v.status === 'planejada' && !v.checklist_feito && (
              <button
                type="button"
                onClick={() => navigate(`/motorista/viagem/${v.id}/checklist`)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600"
              >
                <ClipboardCheck className="w-3.5 h-3.5" /> Fazer checklist
              </button>
            )}

            {v.status === 'planejada' && v.checklist_feito && (
              <button
                type="button"
                onClick={() => abrir(v, 'iniciar')}
                className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white hover:bg-primary-700"
              >
                <Play className="w-3.5 h-3.5" /> Iniciar
              </button>
            )}

            {v.status === 'em_andamento' && (
              <button
                type="button"
                onClick={() => abrir(v, 'finalizar')}
                className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                <Flag className="w-3.5 h-3.5" /> Finalizar
              </button>
            )}

            {v.status !== 'planejada' && v.status !== 'em_andamento' && (
              <span className="flex-shrink-0 text-xs text-secondary-500 whitespace-nowrap">
                {v.km_percorridos ? formatKilometers(v.km_percorridos) : '—'}
              </span>
            )}
          </div>
        ))}
      </div>

      {aberta && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
          <div className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl">
            <div className="px-5 pt-5 pb-3 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">
                {aberta.momento === 'iniciar' ? 'Iniciar viagem' : 'Finalizar viagem'}
              </h3>
              <p className="text-sm text-secondary-600 mt-1">
                {trajeto(aberta.viagem)}
              </p>
              {/* O veiculo foi confirmado (ou trocado) no checklist; aqui ele
                  so aparece, para o km do painel ser lido no carro certo. */}
              {aberta.viagem.veiculo && (
                <p className="text-sm text-secondary-900 font-medium mt-1">
                  Veículo: {aberta.viagem.veiculo.placa} — {aberta.viagem.veiculo.marca} {aberta.viagem.veiculo.modelo}
                </p>
              )}
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* O número vem do painel, e não de memória: pedir "a
                  quilometragem" sem dizer de onde faz o motorista estimar. */}
              {(aberta.momento === 'iniciar' || faltaKmDeSaida) && (
                <div>
                  <KmInput
                    name="km_saida"
                    label="Quilometragem do painel na saída"
                    value={kmSaida}
                    onChange={(e) => setKmSaida(e.target.value)}
                    required
                  />
                  <DicaUltimoKm ultimo={ultimo} erro={erroKmSaida} carregando={buscandoKm} />
                </div>
              )}

              {aberta.momento === 'finalizar' && (
                <>
                  <div>
                    <KmInput
                      name="km_chegada"
                      label="Quilometragem do painel na chegada"
                      value={kmChegada}
                      onChange={(e) => setKmChegada(e.target.value)}
                      required
                    />
                    <DicaUltimoKm ultimo={ultimo} erro={erroKmChegada} carregando={buscandoKm} />
                  </div>
                  {aberta.viagem.km_saida != null && (
                    <p className="text-xs text-secondary-500">
                      Na saída o painel marcava {formatKilometers(aberta.viagem.km_saida)}. O número de agora
                      precisa ser igual ou maior.
                    </p>
                  )}
                </>
              )}

              <p className="text-xs text-secondary-500">
                Depois de enviar você não consegue alterar. Se errar, avise o gestor — ele corrige pelo
                sistema.
              </p>
            </div>

            <div className="px-5 py-4 border-t border-secondary-200 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAberta(null)}
                disabled={salvando}
                className="rounded-lg border border-secondary-300 py-3 font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={enviar}
                disabled={salvando}
                className="rounded-lg bg-primary-600 py-3 font-semibold text-white hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{salvando ? 'Enviando...' : 'Confirmar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ViagensDoMotorista;
